import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../storeConfig";
import { resetAppState } from "../shared/actions";
import { IRestockItemUI } from "../../interfaces/ConveyorBox/IConveyorBox";

export interface RestockState {
  items: IRestockItemUI[];
  activeContextId: number;
  activeItemId: IRestockItemUI["id"] | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: RestockState = {
  items: [],
  activeContextId: -1,
  activeItemId: null,
  isLoading: false,
  error: null,
};

const isReadyForLocationScan = (item: IRestockItemUI) => item.quantityVerified === item.quantity;

const restockSlice = createSlice({
  name: "restock",
  initialState,
  reducers: {
    setRestockItems: (state, action: PayloadAction<IRestockItemUI[]>) => {
      state.items = action.payload;

      // Optional: if exactly one item is ready, auto-focus it.
      // If multiple are ready, keep the current active item.
      // if (!state.activeItemId) {
      //   const ready = action.payload.filter(isReadyForLocationScan);
      //   if (ready.length === 1) state.activeItemId = ready[0].id;
      // }
    },

    upsertRestockItem: (state, action: PayloadAction<IRestockItemUI>) => {
      const item = action.payload;
      const idx = state.items.findIndex((i) => i.id === item.id);

      if (idx === -1) {
        state.items.push(item);
      } else {
        state.items[idx] = item;
      }

      // If item becomes ready for location scan, set as active
      if (isReadyForLocationScan(item)) {
        state.activeItemId = item.id;
      }
    },

    removeRestockItem: (state, action: PayloadAction<number>) => {
      state.items = state.items.filter((i) => i.id !== action.payload);

      if (state.activeItemId === action.payload) {
        state.activeItemId = null;
      }
    },

    resetRestockState: (state) => {
      state.items = [];
      state.activeItemId = null;
      state.activeContextId = -1;
      state.error = null;
      state.isLoading = false;
    },

    setActiveRestockItemId: (state, action: PayloadAction<number | null>) => {
      state.activeItemId = action.payload;
    },

    setRestockContextId: (state, action: PayloadAction<number>) => {
      state.activeContextId = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(resetAppState, () => initialState);
  },
});

export const {
  setRestockItems,
  upsertRestockItem,
  removeRestockItem,
  resetRestockState,
  setActiveRestockItemId,
  setRestockContextId,
} = restockSlice.actions;

// selectors
export const restockStateSelector = (s: RootState) => s.restockReducer;
export const restockItemsSelector = (s: RootState) => s.restockReducer.items;
export const activeRestockItemIdSelector = (s: RootState) => s.restockReducer.activeItemId;

export default restockSlice.reducer;
